/**
 * src/engine/jobRegistry.js
 *
 * Maps job action.operation → handler function.
 * Also exports trigger evaluation helpers.
 */

import { runUpdateStatus } from './jobs/updateStatus.js';
import { closeTask, isValidTaskTransition, systemClosure } from '../utils/taskClosure.js';
import { runVendorRegistrySync } from './jobs/vendorRegistrySync.js';
import { runCreateTaskIfMissing } from './jobs/createTaskIfMissing.js';

// ── Trigger helpers ───────────────────────────────────────────────────────────

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function isOverdueBy(dateStr, days) {
  if (!dateStr) return false;
  const diff = Date.now() - new Date(dateStr).getTime();
  return diff >= days * MS_PER_DAY;
}

function expiresWithin(dateStr, days) {
  if (!dateStr) return false;
  const target = new Date(dateStr).getTime();
  const now = Date.now();
  if (target < now) return false;
  return target - now <= days * MS_PER_DAY;
}

/**
 * Returns entities from state that match a condition trigger.
 * entity type: 'document' | 'task' | 'risk'
 */
export function getMatchingEntities(trigger, state) {
  const { condition } = trigger;
  if (!condition) return [];

  const { entity, field, operator, value } = condition;
  const entityType = entity;

  let items = [];
  if (entityType === 'document') items = state.documents || [];
  else if (entityType === 'task') items = state.adhocTasks || [];
  else if (entityType === 'risk') items = state.risks || [];
  else if (entityType === 'vendor') items = state.vendors || [];

  return items
    .map((item) => ({ ...item, _entityType: entityType }))
    .filter((item) => {
      if (operator === 'overdue_by') return isOverdueBy(item[field], value ?? 0);
      if (operator === 'expires_within') return expiresWithin(item[field], value ?? 0);
      if (operator === 'equals') return item[field] === value;
      if (operator === 'not_equals') return item[field] !== value;
      return false;
    });
}

/**
 * Check whether a schedule trigger is due given lastRunAt.
 */
export function isScheduleDue(schedule, lastRunAt) {
  if (!lastRunAt) return true; // never run → always due

  const elapsed = Date.now() - new Date(lastRunAt).getTime();

  const intervals = {
    hourly: 1 * 60 * 60 * 1000,
    daily: 1 * MS_PER_DAY,
    weekly: 7 * MS_PER_DAY,
    monthly: 30 * MS_PER_DAY,
  };

  return elapsed >= (intervals[schedule] || MS_PER_DAY);
}

// ── Action handlers ───────────────────────────────────────────────────────────

/**
 * Execute a state_logic action.
 * Returns { changed, summary, stateMutations }
 *
 * stateMutations is an array of functions (state) => void
 * that useEngine applies via updateState.
 */
export function executeStateLogic(job, matchedEntities, state) {
  const { operation, config } = job.action;
  const mutations = [];
  const summaries = [];
  let anyChanged = false;

  // Pass schedule context so recurring task logic can anchor due dates
  const _jobContext = job.trigger?.schedule ? { schedule: job.trigger.schedule } : null;

  if (operation === 'update_status') {
    // update_status mutates matched entities in-place
    const entityType = job.trigger?.condition?.entity;
    // An ad-hoc task's status moves only along the one transition table, and
    // a close carries a system closure record naming this job (GH #7704) —
    // the same rule state-write enforces. A task the job may not move is left
    // alone and is not counted as updated.
    const taskStatusJob = entityType === 'task' && (config.targetField || 'status') === 'status';
    const eligible = taskStatusJob
      ? matchedEntities.filter((e) => isValidTaskTransition(e.status || 'open', config.newValue))
      : matchedEntities;
    const cloned = eligible.map((e) => ({ ...e }));
    const result = runUpdateStatus(config, cloned, state);
    // A refused move is reported as refused — never folded into "already
    // set", which would claim in the run log that the job did its work.
    const refused = matchedEntities.length - eligible.length;
    if (refused > 0) {
      result.summary =
        (result.changed ? `${result.summary}; ` : '') +
        `Refused ${refused} task(s): moving a task's status to "${config.newValue}" is not an ` +
        'allowed transition, so nothing was changed for them';
    }

    if (result.changed) {
      anyChanged = true;
      mutations.push((s) => {
        const key =
          entityType === 'document'
            ? 'documents'
            : entityType === 'task'
              ? 'adhocTasks'
              : entityType === 'risk'
                ? 'risks'
                : null;
        if (!key) return;
        s[key] = (s[key] || []).map((item) => {
          if (!result.updatedIds.includes(item.id)) return item;
          if (taskStatusJob && config.newValue === 'closed') {
            return closeTask(
              item,
              systemClosure({
                reason: `Closed by the automation job "${job.name || job.id}"`,
                trigger: `engine:${job.id}`,
              })
            );
          }
          return { ...item, [config.targetField || 'status']: config.newValue };
        });
      });
    }
    summaries.push(result.summary);
  }

  if (operation === 'create_task_if_missing') {
    const result = runCreateTaskIfMissing(job, config, matchedEntities, state);
    if (result.changed) {
      anyChanged = true;
      mutations.push((s) => {
        s.adhocTasks = [...result.newTasks, ...(s.adhocTasks || [])];
      });
    }
    summaries.push(result.summary);
  }

  // RETIRED. This operation used to persist `treatmentSetup.inEffect` — a
  // derived value written back into tenant state, which is the pattern
  // FIND-B-002 and FIND-B-005 forbid in the sibling findings module and which
  // TRYGGR_INTENT.md §10's "Enter Once, Know Everywhere" rules out: two
  // surfaces could disagree about whether a treatment was in effect, and the
  // cached one won. In-effect is now derived on read, every time, by
  // `isRiskSdlcInEffect`.
  //
  // The branch stays so a tenant that still has this job configured gets an
  // explicit reason in its engine log rather than a silent no-run. Removing it
  // would make a configured job vanish without saying anything.
  if (operation === 'check_sdlc_in_effect') {
    summaries.push('Retired — SDLC in-effect is derived on read, not stored');
  }

  return {
    changed: anyChanged,
    summary: summaries.join('; ') || 'No changes',
    mutations,
  };
}

/**
 * Execute a vendor_sync action asynchronously.
 *
 * `tenantId` is required, not optional: the handler writes vendor records
 * through vendors-write and refuses the run without one (GH #6185).
 *
 * Returns Promise<{ changed, summary, mutations }>
 */
export async function executeVendorSync(job, state, { tenantId } = {}) {
  return runVendorRegistrySync(job.action.config || {}, state, { tenantId });
}
